import { inject, Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

import { AnalyticsService } from './analytics.service';
import {
  buildRouteName,
  currentLocationPath,
  sanitizePath,
} from './rum-route.util';

/** Prefix used for the Core Web Vitals metric names reported to the analytics backend. */
export const WEB_VITALS_METRIC_PREFIX = 'WebVitals';

/** Name of the custom event emitted for each Core Web Vital measurement. */
export const WEB_VITALS_EVENT_NAME = 'WebVitals';

/**
 * Reports Core Web Vitals (LCP, INP, CLS, FCP, TTFB) to the analytics backend
 * so they can power a Real User Monitoring dashboard.
 *
 * Each measurement is sent both as a numeric metric (`WebVitals/<name>`, for
 * percentile aggregation) and as a custom event (`WebVitals`, carrying the
 * rating/page so it can be sliced in dashboards).
 */
@Injectable()
export class WebVitalsService {
  private analytics = inject(AnalyticsService);

  private router = inject(Router, { optional: true });

  private initialized = false;

  /**
   * Starts listening for Core Web Vitals. Safe to call multiple times; only the
   * first call registers the listeners.
   */
  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const report = (metric: Metric): void => this.report(metric);

    onLCP(report);
    onINP(report);
    onCLS(report);
    onFCP(report);
    onTTFB(report);
  }

  /**
   * Maps a web-vitals metric to a tracked metric and event.
   *
   * `routeName` (stable template, e.g. `/orders/:id`) and `page` (concrete
   * scrubbed path) are captured here, at report time, mirroring the other RUM
   * collectors so Core Web Vitals group by route consistently. Note the caveat:
   * `web-vitals` reports CLS/LCP/INP once, at page hide, as a page-lifetime
   * value; in this SPA that value can span several routes, so the route captured
   * here is the one active when the metric is flushed ("last route wins"). It is
   * accurate for the early metrics (FCP/TTFB fire at load), and gives consistent
   * grouping for the rest — but it is not true per-shift attribution.
   * @param metric
   */
  private report(metric: Metric): void {
    const properties = {
      metric: metric.name,
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      navigationType: metric.navigationType,
      routeName: buildRouteName(this.router),
      page: sanitizePath(currentLocationPath()),
    };

    this.debugLog(metric);

    this.analytics.trackMetric({
      name: `${WEB_VITALS_METRIC_PREFIX}/${metric.name}`,
      average: metric.value,
      properties,
    });

    this.analytics.trackEvent({
      name: WEB_VITALS_EVENT_NAME,
      properties,
    });
  }

  /**
   * Logs each vital to the console for quick local before/after checks. Active in
   * dev mode automatically; on an optimized/prod build (e.g. a local
   * `--configuration=production` run, where dev mode is off) enable it with
   * `localStorage.setItem('debugWebVitals','1')` or the `?debugWebVitals` query
   * param — so it never spams real users.
   * @param metric
   */
  private debugLog(metric: Metric): void {
    let enabled = isDevMode();
    if (!enabled) {
      try {
        enabled =
          window.localStorage?.getItem('debugWebVitals') === '1' ||
          (window.location?.search.includes('debugWebVitals') ?? false);
      } catch {
        enabled = false;
      }
    }
    if (!enabled) {
      return;
    }

    const icon =
      metric.rating === 'good'
        ? '🟢'
        : metric.rating === 'needs-improvement'
        ? '🟡'
        : '🔴';
    const value =
      metric.name === 'CLS'
        ? metric.value.toFixed(3)
        : `${Math.round(metric.value)}ms`;

    // eslint-disable-next-line no-console
    console.log(
      `${icon} [web-vitals] ${metric.name} = ${value} (${metric.rating}) — ${window.location?.pathname}`,
    );
  }
}
